/**
 * useBackendHealth Hook
 *
 * Monitors backend health via TanStack Query with exponential backoff.
 * Returns isBackendReady: true once the backend responds successfully.
 */

import { useQuery } from '@tanstack/react-query';
import { checkBackendHealth } from '../api/client';

interface UseBackendHealthResult {
  isBackendReady: boolean;
  isChecking: boolean;
  retryCount: number;
  checkNow: () => void;
}

export function useBackendHealth(): UseBackendHealthResult {
  const {
    data: isBackendReady = false,
    isFetching: isChecking,
    failureCount: retryCount,
    refetch: checkNow,
  } = useQuery({
    queryKey: ['backendHealth'],
    queryFn: () => checkBackendHealth(),
    retry: true,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 10000),
    refetchInterval: (query) => (query.state.data === true ? 30_000 : false),
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  return {
    isBackendReady,
    isChecking,
    retryCount,
    checkNow,
  };
}
